import { useMemo, useState } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useAppStore } from "../store/appStore";
import appStyles from "../styles/styles";
import type { RecordingFile } from "../types/models";
import { RecordingHistoryHeader } from "./RecordingHistoryHeader";
import { RecordingItem } from "./RecordingItem";
import { SearchBar } from "./SearchBar";

const RecordingHistory = () => {
	// Access store using individual selectors for React 19 compatibility
	const recordings = useAppStore((state) => state.recordings);
	const deleteRecording = useAppStore((state) => state.deleteRecording);
	const clearRecordings = useAppStore((state) => state.clearRecordings);
	const isLoadingRecordings = useAppStore((state) => state.isLoadingRecordings);
	const uiLanguage = useAppStore((state) => state.uiLanguage);
	const t = useTranslation();
	const [expandedId, setExpandedId] = useState<string | null>(null);
	const [selectedRecordings, setSelectedRecordings] = useState<Set<string>>(new Set());
	const [searchTerm, setSearchTerm] = useState("");

	// Filter recordings based on search term
	const filteredRecordings = useMemo(() => {
		if (!searchTerm.trim()) {
			return [...recordings].sort((a, b) => {
				return b.timestamp.localeCompare(a.timestamp);
			});
		}

		const searchLower = searchTerm.toLowerCase();
		return [...recordings]
			.filter((recording) => {
				return (
					recording.file.name.toLowerCase().includes(searchLower) ||
					recording.transcriptionText?.toLowerCase().includes(searchLower)
				);
			})
			.sort((a, b) => {
				return b.timestamp.localeCompare(a.timestamp);
			});
	}, [recordings, searchTerm]);

	// Toggle selection of a recording
	const toggleSelection = (id: string, e: React.MouseEvent) => {
		e.stopPropagation();
		const newSelected = new Set(selectedRecordings);
		if (newSelected.has(id)) {
			newSelected.delete(id);
		} else {
			newSelected.add(id);
		}
		setSelectedRecordings(newSelected);
	};

	// Select/deselect all recordings
	const toggleSelectAll = () => {
		if (selectedRecordings.size === filteredRecordings.length) {
			setSelectedRecordings(new Set());
		} else {
			setSelectedRecordings(new Set(filteredRecordings.map((r) => r.id)));
		}
	};

	// Download selected recordings
	const downloadSelectedRecordings = async () => {
		if (selectedRecordings.size === 0) return;

		if (selectedRecordings.size === 1) {
			// If only one recording is selected, download it directly
			const recordingId = Array.from(selectedRecordings)[0];
			const recording = recordings.find((r) => r.id === recordingId);
			if (recording) {
				downloadFile(recording);
			}
			return;
		}

		try {
			// Download each file individually
			for (const recordingId of selectedRecordings) {
				const recording = recordings.find((r) => r.id === recordingId);
				if (recording) {
					downloadFile(recording);
					// Add a small delay between downloads to prevent browser issues
					await new Promise((resolve) => setTimeout(resolve, 500));
				}
			}
		} catch (error) {
			console.error("Error downloading files:", error);
			alert(t.downloadFilesError);
		}
	};

	// Download selected transcriptions
	const downloadSelectedTranscriptions = async () => {
		if (selectedRecordings.size === 0) return;

		let downloadCount = 0;

		for (const recordingId of selectedRecordings) {
			const recording = recordings.find((r) => r.id === recordingId);
			if (recording?.transcriptionText?.trim()) {
				downloadTranscriptionText(recording.transcriptionText, recording.timestamp);
				downloadCount++;

				// Add a small delay between downloads to prevent browser issues
				await new Promise((resolve) => setTimeout(resolve, 300));
			}
		}

		if (downloadCount === 0) {
			alert(t.noTranscriptionInSelection);
		}
	};

	// Delete selected recordings
	const deleteSelectedRecordings = () => {
		if (selectedRecordings.size === 0) return;

		// We're using recordingItem.deleteConfirmation as there's no specific message for multiple items
		if (confirm(`${t.recordingItem.deleteConfirmation}`)) {
			for (const id of selectedRecordings) {
				deleteRecording(id);
			}
			setSelectedRecordings(new Set());
		}
	};

	// Format duration in MM:SS or HH:MM:SS format
	const formatDuration = (seconds: number) => {
		if (seconds < 3600) {
			// Less than 1 hour: MM:SS
			const minutes = Math.floor(seconds / 60);
			const remainingSeconds = seconds % 60;
			return `${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
		}
		// 1 hour or more: HH:MM:SS
		const hours = Math.floor(seconds / 3600);
		const minutes = Math.floor((seconds % 3600) / 60);
		const remainingSeconds = seconds % 60;
		return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}:${remainingSeconds.toString().padStart(2, "0")}`;
	};

	// Format file size
	const formatFileSize = (file: File) => {
		const sizeInKB = file.size / 1024;
		if (sizeInKB < 1000) {
			return `${sizeInKB.toFixed(1)} KB`;
		}
		return `${(sizeInKB / 1024).toFixed(2)} MB`;
	};

	// Download file (MP3 if available, otherwise WebM)
	const downloadFile = (recording: RecordingFile) => {
		// MP3ファイルが利用可能ならそちらを優先
		const fileToDownload = recording.mp3File || recording.file;

		const url = URL.createObjectURL(fileToDownload);
		const a = document.createElement("a");
		a.href = url;
		a.download = fileToDownload.name;
		document.body.appendChild(a);
		a.click();
		setTimeout(() => {
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		}, 100);
	};

	// Download transcription text as a file
	const downloadTranscriptionText = (text: string, timestamp: string) => {
		if (!text) {
			alert(t.noTranscriptionAvailable);
			return;
		}

		const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
		const url = URL.createObjectURL(blob);
		const a = document.createElement("a");
		a.href = url;
		// タイムスタンプはそのまま使用（すでに保存済みのフォーマット）
		const filenameBase = timestamp;
		a.download = `transcription_${filenameBase}.txt`;
		document.body.appendChild(a);
		a.click();
		setTimeout(() => {
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		}, 100);
	};

	// Format date for display
	const formatDate = (dateStr: string) => {
		try {
			// T区切りのタイムスタンプを解析: 2025-05-30T14-30-45 のようなフォーマット
			const [datePart, timePart] = dateStr.split("T");
			if (!datePart || !timePart) return dateStr;

			const [year, month, day] = datePart.split("-").map(Number);
			const [hours, minutes, seconds] = timePart.split("-").map(Number);

			if (
				Number.isNaN(year) ||
				Number.isNaN(month) ||
				Number.isNaN(day) ||
				Number.isNaN(hours) ||
				Number.isNaN(minutes) ||
				Number.isNaN(seconds)
			) {
				// 古いフォーマット（ISO文字列ベース）の場合はそのまま変換を試みる
				const date = new Date(dateStr.replace(/-/g, ":"));
				return new Intl.DateTimeFormat(uiLanguage, {
					year: "numeric",
					month: "2-digit",
					day: "2-digit",
					hour: "2-digit",
					minute: "2-digit",
				}).format(date);
			}

			// 新しいフォーマットの場合は直接Dateオブジェクトを作成
			const date = new Date(year, month - 1, day, hours, minutes, seconds);
			return new Intl.DateTimeFormat(uiLanguage, {
				year: "numeric",
				month: "2-digit",
				day: "2-digit",
				hour: "2-digit",
				minute: "2-digit",
			}).format(date);
		} catch (error) {
			console.error("日付フォーマットエラー:", error, dateStr);
			return dateStr;
		}
	};

	// Count how many selected recordings have transcriptions
	const selectedWithTranscriptionCount = useMemo(() => {
		let count = 0;
		for (const id of selectedRecordings) {
			const recording = recordings.find((r) => r.id === id);
			if (recording?.transcriptionText?.trim()) {
				count++;
			}
		}
		return count;
	}, [selectedRecordings, recordings]);
	// ローディング中の表示
	if (isLoadingRecordings) {
		return (
			<div className={`${appStyles.recordingHistoryEmpty} p-10`}>
				<div className={`${appStyles.spinner} mb-6`} />
				<p className={`${appStyles.recordingHistoryEmptyText} text-lg`}>{t.recordingHistoryEmpty.loading}</p>
			</div>
		);
	}

	// 録音がない場合の表示
	if (!recordings || recordings.length === 0) {
		return (
			<div className={`${appStyles.recordingHistoryEmpty} p-10`}>
				<p className={`${appStyles.recordingHistoryEmptyText} text-xl mb-3`}>{t.recordingHistoryEmpty.noRecordings}</p>
				<p className={`${appStyles.recordingHistoryEmptySubText} text-base opacity-80`}>
					{t.recordingHistoryEmpty.startRecording}
				</p>
			</div>
		);
	}
	return (
		<div className={`${appStyles.recordingHistoryList} px-3 py-4 max-w-full overflow-hidden`}>
			<RecordingHistoryHeader
				totalRecordings={recordings.length}
				filteredRecordings={filteredRecordings}
				selectedRecordings={selectedRecordings}
				selectedWithTranscriptionCount={selectedWithTranscriptionCount}
				toggleSelectAll={toggleSelectAll}
				downloadSelectedRecordings={downloadSelectedRecordings}
				downloadSelectedTranscriptions={downloadSelectedTranscriptions}
				deleteSelectedRecordings={deleteSelectedRecordings}
				clearRecordings={clearRecordings}
			/>

			{/* Search box */}
			<SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />

			{filteredRecordings.length === 0 ? (
				<div className={`${appStyles.recordingHistoryEmpty} my-8 py-10`}>
					<p className={`${appStyles.recordingHistoryEmptyText} text-lg`}>
						{t.recordingHistoryEmpty.noMatchingRecordings}
					</p>
				</div>
			) : (
				<div className={`${appStyles.recordingHistoryItemsContainer} my-4 relative`}>
					{filteredRecordings.map((recording) => (
						<RecordingItem
							key={recording.id}
							recording={recording}
							expandedId={expandedId}
							setExpandedId={setExpandedId}
							selectedRecordings={selectedRecordings}
							toggleSelection={toggleSelection}
							downloadFile={downloadFile}
							downloadTranscriptionText={downloadTranscriptionText}
							deleteRecording={deleteRecording}
							formatDate={formatDate}
							formatDuration={formatDuration}
							formatFileSize={formatFileSize}
						/>
					))}
				</div>
			)}
		</div>
	);
};

// Export both as named and default export
export { RecordingHistory };
export default RecordingHistory;
